import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Search, Mail, Shield, ShieldCheck, 
  User, Eye, ChevronDown, ChevronUp, X, Send,
  GraduationCap, Building2, MapPin, Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';

const AdminUsersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [emailModal, setEmailModal] = useState({ open: false, user: null });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const promoteToEditor = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/promote-editor`);
      toast.success('Usuário promovido a Editor!');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao promover usuário');
    }
  };

  const verifyEditor = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/verify-editor`);
      toast.success('Editor verificado!');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao verificar editor');
    }
  };

  const promoteToAdmin = async (userId) => {
    if (!window.confirm('Tem certeza que deseja promover este usuário a ADMIN? Ele terá acesso total ao sistema.')) {
      return;
    }
    try {
      await api.post(`/api/admin/users/${userId}/promote-admin`);
      toast.success('Usuário promovido a Admin!');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao promover usuário');
    }
  };

  const demoteToResearcher = async (userId) => {
    if (!window.confirm('Tem certeza que deseja rebaixar este usuário a Pesquisador?')) {
      return;
    }
    try {
      await api.post(`/api/admin/users/${userId}/demote`);
      toast.success('Usuário rebaixado a Pesquisador');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao rebaixar usuário');
    }
  };

  const openEmailModal = (targetUser) => {
    setEmailModal({ open: true, user: targetUser });
    setEmailSubject('');
    setEmailMessage('');
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Preencha o assunto e a mensagem');
      return;
    }

    setSendingEmail(true);
    try {
      await api.post(`/api/admin/users/${emailModal.user.user_id}/send-email`, {
        subject: emailSubject,
        message: emailMessage
      });
      toast.success('Email enviado com sucesso!');
      setEmailModal({ open: false, user: null });
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getRoleBadge = (userRole, verifiedEditor) => {
    switch (userRole) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
      case 'editor':
        return verifiedEditor 
          ? <Badge className="bg-green-100 text-green-700">Editor Verificado</Badge>
          : <Badge className="bg-yellow-100 text-yellow-700">Editor</Badge>;
      default:
        return <Badge variant="outline">Pesquisador</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      (u.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (u.institution?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
              <Users className="h-6 w-6" />
              Gerenciar Usuários
            </h1>
          </div>
          <Badge variant="outline" className="text-sm">
            {users.length} usuários
          </Badge>
        </div>
      </header>

      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 w-full">
        {/* Filters */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, email ou instituição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white"
            >
              <option value="all">Todos os tipos</option>
              <option value="researcher">Pesquisadores</option>
              <option value="editor">Editores</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {filteredUsers.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-lg p-12 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum usuário encontrado</p>
            </div>
          ) : (
            filteredUsers.map((u) => (
              <div 
                key={u.user_id}
                className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden"
              >
                {/* User Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-bold">
                      {u.name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {u.name || 'Sem nome'}
                        {getRoleBadge(u.role, u.verified_editor)}
                      </div>
                      <div className="text-sm text-gray-500">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEmailModal(u);
                      }}
                      title="Enviar email"
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    {expandedUser === u.user_id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedUser === u.user_id && (
                  <div className="border-t border-[#E4E4E7] p-4 bg-gray-50">
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <GraduationCap className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Titulação:</span>
                          <span>{u.titulacao || 'Não informado'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Instituição:</span>
                          <span>{u.institution || 'Não informado'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Localização:</span>
                          <span>{u.city && u.state ? `${u.city}, ${u.state}` : 'Não informado'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-500">Cadastro:</span>
                          <span>{formatDate(u.created_at)}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="text-gray-500">Áreas de interesse:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {u.knowledge_areas?.length > 0 ? (
                              u.knowledge_areas.slice(0, 5).map((area, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {area}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-gray-400">Não informado</span>
                            )}
                            {u.knowledge_areas?.length > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{u.knowledge_areas.length - 5}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Alertas semanais:</span>
                          <Badge className={u.weekly_alerts_enabled ? 'bg-green-100 text-green-700 ml-2' : 'bg-gray-100 text-gray-700 ml-2'}>
                            {u.weekly_alerts_enabled ? 'Ativados' : 'Desativados'}
                          </Badge>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Perfil completo:</span>
                          <Badge className={u.profile_completed ? 'bg-green-100 text-green-700 ml-2' : 'bg-yellow-100 text-yellow-700 ml-2'}>
                            {u.profile_completed ? 'Sim' : 'Não'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                      {u.role === 'researcher' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => promoteToEditor(u.user_id)}
                          className="gap-2"
                        >
                          <Shield className="h-4 w-4" />
                          Promover a Editor
                        </Button>
                      )}
                      {u.role === 'editor' && !u.verified_editor && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => verifyEditor(u.user_id)}
                          className="gap-2 text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          Verificar Editor
                        </Button>
                      )}
                      {u.role !== 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => promoteToAdmin(u.user_id)}
                          className="gap-2 text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Shield className="h-4 w-4" />
                          Promover a Admin
                        </Button>
                      )}
                      {u.role !== 'researcher' && u.user_id !== user?.user_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => demoteToResearcher(u.user_id)}
                          className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <User className="h-4 w-4" />
                          Rebaixar a Pesquisador
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEmailModal(u)}
                        className="gap-2"
                      >
                        <Mail className="h-4 w-4" />
                        Enviar Email
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Email Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Enviar Email</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEmailModal({ open: false, user: null })}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-1">Para:</div>
              <div className="font-medium">{emailModal.user?.email}</div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Assunto</label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Assunto do email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mensagem</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Digite sua mensagem..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setEmailModal({ open: false, user: null })}
              >
                Cancelar
              </Button>
              <Button
                onClick={sendEmail}
                disabled={sendingEmail}
                className="bg-[#002FA7] gap-2"
              >
                {sendingEmail ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Enviar
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminUsersPage;
